import React, { useState, useRef, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { Badge } from '../ui/Badge';
import client from '../../api/client';
import { useAuth } from '../../auth/useAuth';

interface UserProfile {
  displayName: string;
  email: string;
  roles: string[];
  profilePhoto?: string;
  initials: string;
}

interface MeResponse {
  display_name: string;
  email: string;
  profile_photo_url?: string | null;
  ou_memberships?: string[];
}

interface AccessGroupSummary {
  entra_object_id: string;
  roles: string[];
  is_active: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
};

const initialsFrom = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const useUserProfile = (): UserProfile | null => {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, groupsRes] = await Promise.all([
          client.get<MeResponse>('/auth/me'),
          client.get<{ items: AccessGroupSummary[] }>(
            '/access-groups?include_inactive=false'
          ),
        ]);
        if (cancelled) return;
        const me = meRes.data;
        const ous = (me.ou_memberships || []).map((o) => o.toLowerCase());
        const effectiveRoles = new Set<string>();
        for (const g of groupsRes.data.items || []) {
          if (!g.is_active) continue;
          if (ous.includes(g.entra_object_id.toLowerCase())) {
            for (const r of g.roles || []) effectiveRoles.add(r);
          }
        }
        const roleLabels = Array.from(effectiveRoles).map(
          (r) => ROLE_LABELS[r] || r
        );
        setProfile({
          displayName: me.display_name,
          email: me.email,
          roles: roleLabels.length > 0 ? roleLabels : ['User'],
          profilePhoto: me.profile_photo_url || undefined,
          initials: initialsFrom(me.display_name),
        });
      } catch (err) {
        console.error('Failed to load user profile', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return profile;
};

// Bug #260: the previous implementation POSTed to /api/v1/auth/logout and
// then set ``window.location.pathname = '/login'``. Two problems:
//
//   1. MSAL still held the cached account, so the route guard saw
//      ``accounts.length > 0`` and immediately redirected back to /. The
//      user briefly saw /login flash by, then bounced home, still
//      authenticated.
//   2. The backend ``/auth/logout`` endpoint is a no-op for our token-
//      based auth — there's no session cookie to clear server-side.
//
// The fix: route the click through ``useAuth().logout()`` which calls
// ``instance.logoutRedirect()``. That clears the MSAL cache, hits Entra's
// logout endpoint, and redirects the browser back to the registered
// post-logout URI — which is the only flow that actually drops the
// account so the next render sees ``accounts.length === 0``.

export const UserProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const user = useUserProfile();
  const { logout } = useAuth();

  const displayUser = user || {
    displayName: 'Loading…',
    email: '',
    roles: [],
    initials: '…',
    profilePhoto: undefined as string | undefined,
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen]);

  const avatarButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'transform 0.2s ease-in-out',
  };

  const avatarStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'var(--color-magenta)',
    color: 'var(--color-white)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 200,
    minWidth: 280,
    display: isOpen ? 'block' : 'none',
  };

  const userInfoStyle: React.CSSProperties = {
    padding: '16px',
    borderBottom: '1px solid var(--color-light-grey)',
  };

  const userNameStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'var(--font-family)',
    color: 'var(--color-black)',
    margin: '0 0 4px 0',
  };

  const userEmailStyle: React.CSSProperties = {
    fontSize: '12px',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-dark-grey)',
    margin: '0 0 8px 0',
  };

  const rolesWrapperStyle: React.CSSProperties = {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  };

  const dividerStyle: React.CSSProperties = {
    height: '1px',
    backgroundColor: 'var(--color-light-grey)',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--color-white)',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-dark-grey)',
    width: '100%',
    textAlign: 'left',
    transition: 'background-color 0.2s ease-in-out',
    textDecoration: 'none',
  };

  const dangerMenuItemStyle: React.CSSProperties = {
    ...menuItemStyle,
    color: '#dc3545',
  };

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      <button
        style={avatarButtonStyle}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User profile menu"
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }}
      >
        <div style={avatarStyle}>{displayUser.initials}</div>
      </button>

      <div style={panelStyle} role="menu">
        <div style={userInfoStyle}>
          <h3 style={userNameStyle}>{displayUser.displayName}</h3>
          <p style={userEmailStyle}>{displayUser.email}</p>
          <div style={rolesWrapperStyle}>
            {displayUser.roles.map((role) => (
              <Badge
                key={role}
                variant={role.toLowerCase().includes('admin') ? 'admin' : 'user'}
              >
                {role}
              </Badge>
            ))}
          </div>
        </div>

        <div style={dividerStyle} />

        <a
          style={menuItemStyle}
          onClick={() => {
            window.location.pathname = '/profile';
            setIsOpen(false);
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              'var(--color-light-grey)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              'var(--color-white)';
          }}
          role="menuitem"
        >
          My profile
        </a>

        <button
          style={dangerMenuItemStyle}
          onClick={() => logout()}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              'rgba(220, 53, 69, 0.1)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              'var(--color-white)';
          }}
          role="menuitem"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </div>
  );
};
